/**
 * Deciding whether a 401 means "your session ended" or "that request failed".
 *
 * The JWT lasts 30 days with no refresh, and the response interceptor used to
 * ignore 401s entirely. So on expiry the app still looked signed in — the shell
 * rendered, the user tapped around, and every request failed silently. They saw
 * empty screens, never a login form. On a phone, which stays signed in for
 * months and hits this mid-visit, that means losing captured work with no
 * explanation.
 *
 * Not every 401 is an expiry, though. Signing in with the wrong password is a
 * 401 from `/users/login`, and treating that as an expiry would clear the
 * session and bounce the user back to the login screen they are already on —
 * a loop with no error message.
 */

/**
 * Request paths where a 401 is the endpoint's own answer, not a dead session.
 *
 * Matched as substrings against the request URL, which is relative to the axios
 * instance's `baseURL` (`/users/login`, not the full origin).
 */
const AUTH_PATHS = [
	'/users/login',
	'/users/magic-login',
	'/users/verify-code',
	'/users/send-verification-code',
	'/users/convert',
	'/users/invited',
]

/** Is this request one where a 401 is an expected answer? */
export const isAuthRequest = (url: string | null | undefined): boolean => {
	if (!url) return false
	const path = url.split('?')[0]
	return AUTH_PATHS.some(authPath => path.endsWith(authPath) || path === authPath)
}

/**
 * True when this error means the session is over and the user must sign in again.
 *
 * Deliberately narrow: only a 401, and never from an auth endpoint. A 403 is a
 * permissions answer about a live session and must not sign anyone out — the
 * capability model returns those for account-tier limits.
 */
export const isSessionExpiry = (error: any): boolean => {
	const status = error?.response?.status
	if (status !== 401) return false
	return !isAuthRequest(error?.config?.url)
}

/**
 * Where to send the user back to after signing in again.
 *
 * Returns a login URL carrying the attempted path, so a field user who taps a
 * site and hits an expired token lands back on that site rather than at Home.
 * Login paths are never preserved — that would bounce them in a circle.
 */
export const loginUrlPreserving = (currentPath: string): string => {
	const isAlreadyAuthScreen = /^\/(login|register|forgotpassword|changepassword|magic)/.test(
		currentPath,
	)
	if (isAlreadyAuthScreen || currentPath === '/') return '/login'
	return `/login?next=${encodeURIComponent(currentPath)}`
}
